import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Row, Col, FormGroup, FormControl, ButtonGroup, Button, Panel } from 'react-bootstrap';
import { handleError } from '../../../../shared/ErrorCatching';
import { Translate } from '../../../../shared/LanguageService';
import { getPhotoStatus } from '../imageprocessing/actions/ImageProcessingActions';
import { CommentType } from '../imageprocessing/Entities';
import * as Features from '../../../../shared/Features';
import * as imageProcessingActions from '../imageprocessing/actions/ImageProcessingActions';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import SuccessSnackbar from '../../../../shared/feedback/SuccessSnackbar';
import WarningSnackbar from '../../../../shared/feedback/WarningSnackbar';
import FailureSnackbar from '../../../../shared/feedback/FailureSnackbar';
import OrderInfo from './orderInfo/OrderInfo';
import OrderRowList from './orderrow/OrderRowList';
import Modal from './BasicOrderInfoModal';
import OrderRowForm from './orderrow/OrderRowForm';
import EmailList from './communications/email/view/EmailList';
import SmsList from './communications/sms/view/SmsList';
import EmailModal from './communications/email/create/EmailModal';
import CommunicationDetailsForm from './communications/CommunicationDetailsForm';
import * as Actions from './Actions';
import SmsModal from './communications/sms/create/SmsModal';
import OrderCommunications from './OrderCommunications';
import { OrderTypes } from './Types';
import {
    selectBasicOrderInfo,
    selectBasicOrderInfoSms
} from './Reducer';
import {
    selectViewOrderDigitalStylingOrderStatus,
    selectViewOrderProductProperties
} from '../Reducer';
import { useFeedbackSnackbar } from './hooks/useFeedbackSnackbar';
import { useBasicOrderInfoData } from './hooks/useBasicOrderInfoData';
import { useBasicOrderInfoSuspense } from './hooks/useBasicOrderInfoSuspense';
import { useImageProcessingInfoSuspense } from './hooks/useImageProcessingInfoSuspense';
import { isDigitalStylingOrder } from './orderTypeChecks';
import OrderRowListSkeleton from './skeletons/OrderRowListSkeleton';
import OrderInfoSkeleton from './skeletons/OrderInfoSkeleton';
import OrderCommunicationsSkeleton from './skeletons/OrderCommunicationsSkeleton';
import InternalCommentSkeleton from './skeletons/InternalCommentSkeleton';
import EmailListSkeleton from './skeletons/EmailListSkeleton';
import { useExtraDeliveryRecipientsDraft } from './hooks/useExtraDeliveryRecipientsDraft';
import { useImageProcessingCommentsDraft } from './hooks/useImageProcessingCommentsDraft';
import { hasKeyPickupProductSelected, useOrderRowEditing } from './hooks/useOrderRowEditing';
import { useCommunicationModals } from './hooks/useCommunicationModals';
import { useUnbooking } from './hooks/useUnbooking';
import { useOrderStateUpdate } from './hooks/useOrderStateUpdate';

function BasicOrderInfoSuspenseGate(props: { orderId: number; children: React.ReactNode }) {
    useBasicOrderInfoSuspense(props.orderId);
    return <React.Fragment>{props.children}</React.Fragment>;
}

function ImageProcessingCommentsSuspenseGate(props: {
    orderId: number;
    children: React.ReactNode;
}) {
    useImageProcessingInfoSuspense(props.orderId);
    return <React.Fragment>{props.children}</React.Fragment>;
}

export const BasicOrderInfoPage = (props: any) => {
    const { orderId, history } = props;
    const dispatch = useDispatch<any>();
    const store = useStore();

    const order = useSelector(selectBasicOrderInfo);
    const sms = useSelector(selectBasicOrderInfoSms);
    const site = useSelector((state: any) => state.site);
    const user = useSelector((state: any) => state.user);
    const productProperties = useSelector(selectViewOrderProductProperties);
    const digitalStylingOrderStatus = useSelector(selectViewOrderDigitalStylingOrderStatus);
    const imageProcessingCommentsFromStore = useSelector(
        (state: any) => state.imageProcessing.comments
    );

    const [orderSupportsInternalComment, setOrderSupportsInternalComment] =
        React.useState(false);
    const [showArchiveSampleImagesModal, setShowArchiveSampleImagesModal] = React.useState(false);
    const [showSetOrderToUploaderConfirmModal, setShowSetOrderToUploaderConfirmModal] =
        React.useState(false);

    const { feedbackEntries, showSuccess, showWarning, showFailure, dismissFeedback } =
        useFeedbackSnackbar();

    const onOrderLoaded = (): void => {
        const loadedOrder = selectBasicOrderInfo(store.getState());

        setOrderSupportsInternalComment(
            Boolean(loadedOrder) && (loadedOrder.orderType & OrderTypes.plan) === 0
        );
    };

    const { reloadOrder } = useBasicOrderInfoData({ orderId, onOrderLoaded });

    const {
        extraDeliveryRecipients,
        onExtraDeliveryRecipientsChanged,
        markExtraDeliveryRecipientsSaved
    } = useExtraDeliveryRecipientsDraft(order);

    const {
        imageProcessingComments,
        openEditComment,
        cancelComment,
        onChangeComment,
        applySavedComment
    } = useImageProcessingCommentsDraft(imageProcessingCommentsFromStore);

    const {
        selectedProduct,
        orderRow,
        formErrors,
        doing,
        showAddOrderRowModal,
        showEditOrderRowModal,
        showEditOrderRowConfirmModal,
        includeEditOrderRowFileRemovalWarning,
        possibleProductsForUpdate,
        onChange,
        onChangeKeyPickupCheckbox,
        onPhotographerCompensationSetManuallyChange,
        toggleAddOrderRowModal,
        addOrderRow,
        editOrderRow,
        closeEditOrderRowModal,
        updateOrderRow,
        toggleEditRowConfirmation,
        removeOrderRow
    } = useOrderRowEditing({ orderId, order, user, showSuccess, showWarning });

    const {
        showEmailListModal,
        showSmsListModal,
        showCreateMailModal,
        showCreateSmsModal,
        showViewEmailDetailsModal,
        showViewSmsDetailsModal,
        viewCommunicationDetailsRecipients,
        viewCommunicationDetailsBody,
        viewCommunicationDetailsBodyIsHtml,
        viewCommunicationDetailsHasFile,
        viewCommunicationDetailsAttachments,
        openShowEmailListModal,
        closeShowEmailListModal,
        openShowSmsListModal,
        closeShowSmsListModal,
        openCreateMailModal,
        closeCreateMailModal,
        openCreateSmsModal,
        closeCreateSmsModal,
        openViewEmailDetailsModal,
        openViewSmsDetailsModal,
        closeViewCommunicationDetailsModal
    } = useCommunicationModals({ orderId, sms });

    const {
        showUnbookingModal,
        unbookingModalMessage,
        unbookPhotographer,
        performUnbooking,
        toggleUnbookingModal
    } = useUnbooking({ order, user, history, reloadOrder, showSuccess, showFailure });

    const {
        showUpdateOrderStateConfirmation,
        orderStateToConfirm,
        removeOrderStateTitle,
        removeOrderStateText,
        updateOrderState,
        updateOrderStateConfirmation,
        closeUpdateOrderStateConfirmation
    } = useOrderStateUpdate({ orderId, order, productProperties, user, showSuccess });

    const navigateTo = (route: string): void => {
        history.push(route);
    };

    const getCommentType = (type: number): number => {
        const isInternalComment = type === CommentType.INTERNAL;

        return isInternalComment && isDigitalStylingOrder(order)
            ? CommentType.ORDER_INTERNAL_COMMENT
            : type;
    };

    const saveComment = (text: string, type: number): void => {
        const commentType = getCommentType(type);

        dispatch(imageProcessingActions.updateImageProcessingComment(orderId, text, commentType))
            .then(() => {
                showSuccess(Translate('image-processing/notification/comment-updated'));
                applySavedComment();
            })
            .catch((error: string) => handleError(error));
    };

    const markAsUnread = (): void => {
        dispatch(Actions.markAsUnread(orderId)).then(() => {
            showSuccess(
                Translate('view-order/basic-order-info/toastr/mark-as-unread-successful')
            );
        });
    };

    const unarchivePhoto = (): void => {
        dispatch(Actions.unarchivePhoto(orderId, order.site)).then(() => {
            showSuccess(
                Translate('view-order/basic-order-info/toastr/order-unarchive-successful')
            );
        });
    };

    const unarchivePlan = (): void => {
        dispatch(Actions.unarchivePlan(orderId, order.site)).then(() => {
            showSuccess(
                Translate('view-order/basic-order-info/toastr/order-unarchive-successful')
            );
        });
    };

    const ownerCommunicationLanguageChanged = (event: any): void => {
        dispatch(Actions.updateOwnerCommunicationLanguage(orderId, event.target.value)).catch(
            () => {
                showFailure(
                    Translate(
                        'view-order/basic-order-info/communication/owner-language/update-error'
                    )
                );
            }
        );
    };

    const onIsDirectDeliveryChanged = (event: any): void => {
        dispatch(Actions.UpdateIsDirectDelivery(orderId, event.target.checked)).catch(() => {
            showFailure(
                Translate('view-order/basic-order-info/toastr/is-direct-delivery-failed')
            );
        });
    };

    const updateImageEditorFirmChanged = (event: any): void => {
        dispatch(Actions.updateImageEditorFirm(orderId, event.target.value))
            .then(() => {
                showSuccess(
                    Translate('view-order/basic-order-info/titles/image-edit-firm/success')
                );
            })
            .catch(() => {
                dispatch(getPhotoStatus(orderId));
                showFailure(Translate('view-order/basic-order-info/titles/image-edit-firm/error'));
            });
    };

    const updateImageEditorChanged = (event: any): void => {
        dispatch(Actions.updateImageEditor(orderId, event.target.value))
            .then(() => {
                showSuccess(Translate('view-order/basic-order-info/titles/image-editor/success'));
            })
            .catch(() => {
                showFailure(Translate('view-order/basic-order-info/titles/image-editor/error'));
            });
    };

    const updateExtraDeliveryRecipients = (newExtraDeliveryRecipients: string): void => {
        dispatch(Actions.updateExtraDeliveryRecipients(orderId, newExtraDeliveryRecipients))
            .then(() => {
                markExtraDeliveryRecipientsSaved();
                showSuccess(
                    Translate(
                        'view-order/basic-order-info/communication/extra-delivery-recipients/save/success'
                    )
                );
            })
            .catch((error: any) => handleError(error));
    };

    const setOrderToUploader = (): void => {
        dispatch(Actions.setOrderToUploader(orderId))
            .then(() => {
                showSuccess(
                    Translate('view-order/basic-order-info/set-order-to-uploader/success')
                );
                dispatch(getPhotoStatus(orderId));
            })
            .catch((error: any) => handleError(error));
    };

    const lockPhotographer = (): void => {
        dispatch(Actions.lockPhotographer(orderId)).then(() =>
            showSuccess(
                Translate('view-order/basic-order-info/photographer-lock/update/locked/success')
            )
        );
    };

    const unlockPhotographer = (): void => {
        dispatch(Actions.unlockPhotographer(orderId)).then(() =>
            showSuccess(
                Translate(
                    'view-order/basic-order-info/photographer-lock/update/unlocked/success'
                )
            )
        );
    };

    const downloadFile = (warmStorageReference: string): void => {
        dispatch(Actions.downloadFile(warmStorageReference));
    };

    const toggleSetOrderToUploaderConfirmation = (): void => {
        setShowSetOrderToUploaderConfirmModal(!showSetOrderToUploaderConfirmModal);
    };

    const onToggleArchiveSampleImagesModal = (show: boolean): void => {
        setShowArchiveSampleImagesModal(show);
    };

    const getEditOrderRowText = () => {
        if (includeEditOrderRowFileRemovalWarning) {
            return (
                <p>
                    <i className="material-icons">warning</i>{' '}
                    {Translate('view-order/basic-order-info/products/edit-confirmation-with-files')}
                </p>
            );
        }
        return (
            <p>
                <span>{Translate('view-order/basic-order-info/products/edit-confirmation')}</span>
            </p>
        );
    };

    const shouldShowInternalComment =
        orderSupportsInternalComment ||
        imageProcessingComments.editInternalComment ||
        Boolean(imageProcessingComments.internalComment);

    return (
        <div>
            {Features.hasNotFeature(Features.ViewStylingOrder, user) && (
                <Row className="tabular">
                    <React.Suspense fallback={<OrderRowListSkeleton />}>
                        <BasicOrderInfoSuspenseGate orderId={orderId}>
                            <OrderRowList
                                orderId={orderId}
                                order={order}
                                selectedProduct={selectedProduct}
                                onChange={onChange}
                                remove={removeOrderRow}
                                add={toggleAddOrderRowModal}
                                edit={editOrderRow}
                                user={user}
                            />
                        </BasicOrderInfoSuspenseGate>
                    </React.Suspense>
                </Row>
            )}
            <Row className="tabular">
                <React.Suspense fallback={<OrderInfoSkeleton />}>
                    <BasicOrderInfoSuspenseGate orderId={orderId}>
                        <OrderInfo
                    site={site}
                    order={order}
                    digitalStylingOrderStatus={digitalStylingOrderStatus}
                    orderId={orderId}
                    navigateTo={navigateTo}
                    user={user}
                    showArchiveSampleImagesModal={showArchiveSampleImagesModal}
                    onToggleArchiveSampleImagesModal={onToggleArchiveSampleImagesModal}
                    unbookPhotographer={unbookPhotographer}
                    markAsUnread={markAsUnread}
                    unarchivePhoto={unarchivePhoto}
                    unarchivePlan={unarchivePlan}
                    onIsDirectDeliveryChanged={onIsDirectDeliveryChanged}
                    updateImageEditorFirmChanged={updateImageEditorFirmChanged}
                    updateImageEditorChanged={updateImageEditorChanged}
                    setOrderToUploader={toggleSetOrderToUploaderConfirmation}
                    updateOrderState={updateOrderStateConfirmation}
                    lockPhotographer={lockPhotographer}
                    unlockPhotographer={unlockPhotographer}
                    saveComment={saveComment}
                    onBrokerSystemConnectionSaved={() => reloadOrder()}
                />
                    </BasicOrderInfoSuspenseGate>
                </React.Suspense>
                {(Features.hasFeature(Features.ViewOrderCommunications, user) ||
                    Features.hasFeature(Features.CreateOrderCommunication, user)) && (
                    <React.Suspense fallback={<OrderCommunicationsSkeleton />}>
                        <BasicOrderInfoSuspenseGate orderId={orderId}>
                            <OrderCommunications
                        order={order}
                        viewEmails={openShowEmailListModal}
                        viewSms={openShowSmsListModal}
                        createMail={openCreateMailModal}
                        createSms={openCreateSmsModal}
                        user={user}
                        onPreventEmailsToBrokerChange={(event: any) => {
                            dispatch(
                                Actions.updatePreventEmailsToBroker(orderId, event.target.checked)
                            ).catch((error: any) => handleError(error));
                        }}
                        onPreventCommunicationToPropertyOwnerChange={(event: any) => {
                            dispatch(
                                Actions.updatePreventCommunicationToPropertyOwner(
                                    orderId,
                                    event.target.checked
                                )
                            ).catch((error: any) => handleError(error));
                        }}
                        onPreventEmailsToPhotographerChange={(event: any) => {
                            dispatch(
                                Actions.updatePreventEmailsToPhotographer(
                                    orderId,
                                    event.target.checked
                                )
                            ).catch((error: any) => handleError(error));
                        }}
                        ownerCommunication={order.ownerCommunication}
                        ownerCommunicationLanguageChanged={ownerCommunicationLanguageChanged}
                        extraDeliveryRecipients={extraDeliveryRecipients}
                        onExtraDeliveryRecipientsChanged={onExtraDeliveryRecipientsChanged}
                        updateExtraDeliveryRecipients={updateExtraDeliveryRecipients}
                    />
                        </BasicOrderInfoSuspenseGate>
                    </React.Suspense>
                )}
            </Row>
            {shouldShowInternalComment &&
                Features.hasFeature(Features.ViewInformationForSe360, user) && (
                    <React.Suspense fallback={<InternalCommentSkeleton />}>
                        <ImageProcessingCommentsSuspenseGate orderId={orderId}>
                            <Row>
                        <Col xs={12}>
                            <Panel>
                                <Panel.Heading>
                                    <div>
                                        {Translate('image-processing/page/comments/header/internal')}
                                        {Features.hasFeature(
                                            Features.UpdateCommentToPhotographer,
                                            user
                                        ) && (
                                            <Button
                                                bsSize="small"
                                                bsStyle="info"
                                                className="btn-edit"
                                                onClick={() => openEditComment()}
                                            >
                                                <i className="material-icons mode">edit</i>{' '}
                                                {Translate(
                                                    'image-processing/page/comments/header/edit-comment'
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </Panel.Heading>
                                <Panel.Body>
                                    {!imageProcessingComments.editInternalComment &&
                                        imageProcessingComments.internalComment}
                                    {!imageProcessingComments.editInternalComment &&
                                        !imageProcessingComments.internalComment && <span />}
                                    {imageProcessingComments.editInternalComment && (
                                        <div>
                                            <FormGroup>
                                                <FormControl
                                                    name="imageProcessingComments.internalComment"
                                                    componentClass="textarea"
                                                    value={imageProcessingComments.internalComment}
                                                    onChange={onChangeComment}
                                                />
                                            </FormGroup>
                                            <ButtonGroup className="pull-right">
                                                <Button onClick={() => cancelComment()}>
                                                    {Translate(
                                                        'image-processing/page/comment/button-label/cancel'
                                                    )}
                                                </Button>
                                                <Button
                                                    bsStyle="primary"
                                                    onClick={() =>
                                                        saveComment(
                                                            imageProcessingComments.internalComment,
                                                            CommentType.INTERNAL
                                                        )
                                                    }
                                                >
                                                    {Translate(
                                                        'image-processing/page/comment/button-label/save'
                                                    )}
                                                </Button>
                                            </ButtonGroup>
                                        </div>
                                    )}
                                </Panel.Body>
                            </Panel>
                        </Col>
                    </Row>
                        </ImageProcessingCommentsSuspenseGate>
                    </React.Suspense>
                )}
            {showEmailListModal && (
                <Modal
                    title="view-order/basic-order-info/modal/view-emails/title"
                    readOnly={true}
                    large={true}
                    close={closeShowEmailListModal}
                    cancelText="view-order/basic-order-info/modal/view-emails/button-labels/cancel"
                >
                    <React.Suspense fallback={<EmailListSkeleton />}>
                        <EmailList orderId={orderId} viewDetails={openViewEmailDetailsModal} />
                    </React.Suspense>
                </Modal>
            )}
            {showViewEmailDetailsModal && (
                <Modal
                    title="view-order/basic-order-info/modal/view-emails/details/title"
                    readOnly={true}
                    large={false}
                    close={closeViewCommunicationDetailsModal}
                    cancelText="view-order/basic-order-info/modal/view-emails/details/button-labels/cancel"
                >
                    <CommunicationDetailsForm
                        body={viewCommunicationDetailsBody}
                        recipients={viewCommunicationDetailsRecipients}
                        isHtml={viewCommunicationDetailsBodyIsHtml}
                        download={(warmStorageReference: string) =>
                            downloadFile(warmStorageReference)
                        }
                        hasFile={viewCommunicationDetailsHasFile}
                        attachments={viewCommunicationDetailsAttachments}
                    />
                </Modal>
            )}
            {showCreateMailModal && <EmailModal close={closeCreateMailModal} orderId={orderId} />}
            {showSmsListModal && (
                <Modal
                    title="view-order/basic-order-info/modal/view-sms/title"
                    readOnly={true}
                    large={true}
                    close={closeShowSmsListModal}
                    cancelText="view-order/basic-order-info/modal/view-sms/button-labels/cancel"
                >
                    <SmsList items={sms} viewDetails={openViewSmsDetailsModal} />
                </Modal>
            )}
            {showViewSmsDetailsModal && (
                <Modal
                    title="view-order/basic-order-info/modal/view-sms/details/title"
                    readOnly={true}
                    large={false}
                    close={closeViewCommunicationDetailsModal}
                    cancelText="view-order/basic-order-info/modal/view-sms/details/button-labels/cancel"
                >
                    <CommunicationDetailsForm
                        body={viewCommunicationDetailsBody}
                        recipients={viewCommunicationDetailsRecipients}
                        isHtml={false}
                        download={(warmStorageReference: string) =>
                            downloadFile(warmStorageReference)
                        }
                        hasFile={viewCommunicationDetailsHasFile}
                        attachments={viewCommunicationDetailsAttachments}
                    />
                </Modal>
            )}
            {showCreateSmsModal && <SmsModal close={closeCreateSmsModal} orderId={orderId} />}
            {showAddOrderRowModal && (
                <Modal
                    title="view-order/basic-order-info/modal/add-order-row/title"
                    readOnly={false}
                    do={addOrderRow}
                    doText="view-order/basic-order-info/modal/add-order-row/button-labels/do"
                    close={toggleAddOrderRowModal}
                    doing={doing}
                    doingText="view-order/basic-order-info/modal/add-order-row/button-labels/doing"
                    cancelText="view-order/basic-order-info/modal/add-order-row/button-labels/cancel"
                >
                    <OrderRowForm
                        orderType={order.orderType}
                        errors={formErrors}
                        onChange={onChangeKeyPickupCheckbox}
                        orderRow={orderRow}
                        onPhotographerCompensationSetManuallyChange={
                            onPhotographerCompensationSetManuallyChange
                        }
                        possibleProducts={order.customer.possibleProducts}
                        hasKeyProductSelected={hasKeyPickupProductSelected(
                            orderRow,
                            order.customer.possibleProducts
                        )}
                    />
                </Modal>
            )}

            {showEditOrderRowModal && (
                <Modal
                    title="view-order/basic-order-info/modal/edit-order-row/title"
                    readOnly={false}
                    do={toggleEditRowConfirmation}
                    doText="view-order/basic-order-info/modal/edit-order-row/button-labels/do"
                    close={closeEditOrderRowModal}
                    doing={doing}
                    doingText="view-order/basic-order-info/modal/edit-order-row/button-labels/doing"
                    cancelText="view-order/basic-order-info/modal/edit-order-row/button-labels/cancel"
                >
                    <OrderRowForm
                        orderType={order.orderType}
                        errors={formErrors}
                        onChange={onChangeKeyPickupCheckbox}
                        onPhotographerCompensationSetManuallyChange={
                            onPhotographerCompensationSetManuallyChange
                        }
                        orderRow={orderRow}
                        possibleProducts={possibleProductsForUpdate}
                        hasKeyProductSelected={hasKeyPickupProductSelected(
                            orderRow,
                            possibleProductsForUpdate
                        )}
                    />
                </Modal>
            )}
            {showUpdateOrderStateConfirmation && (
                <ConfirmationModal
                    title={Translate(removeOrderStateTitle)}
                    text={Translate(removeOrderStateText)}
                    doButtonLabel={Translate(
                        'view-order/basic-order-info/orderstate/removed/confirmation/do-button'
                    )}
                    cancelButtonLabel={Translate(
                        'view-order/basic-order-info/orderstate/removed/confirmation/cancel-button'
                    )}
                    doButtonStyle="danger"
                    do={() => updateOrderState(orderStateToConfirm)}
                    closeModal={() => closeUpdateOrderStateConfirmation()}
                />
            )}
            {showEditOrderRowConfirmModal && (
                <ConfirmationModal
                    closeModal={() => toggleEditRowConfirmation()}
                    title={Translate('view-order/basic-order-info/modal/edit-order-row/title')}
                    text={getEditOrderRowText()}
                    doButtonLabel={Translate(
                        'view-order/basic-order-info/modal/edit-order-row/button-labels/do'
                    )}
                    cancelButtonLabel={Translate(
                        'view-order/basic-order-info/modal/edit-order-row/button-labels/cancel'
                    )}
                    doButtonStyle="danger"
                    do={() => updateOrderRow()}
                    disableSubmit={true}
                    initialSubmitDisabling={false}
                />
            )}
            {showSetOrderToUploaderConfirmModal && (
                <ConfirmationModal
                    closeModal={() => toggleSetOrderToUploaderConfirmation()}
                    title={Translate('view-order/basic-order-info/set-order-to-uploader/title')}
                    text={Translate('view-order/basic-order-info/set-order-to-uploader/info')}
                    doButtonLabel={Translate(
                        'view-order/basic-order-info/set-order-to-uploader/confirm'
                    )}
                    cancelButtonLabel={Translate(
                        'view-order/basic-order-info/set-order-to-uploader/cancel'
                    )}
                    doButtonStyle={'danger'}
                    do={() => setOrderToUploader()}
                    disableSubmit={true}
                    initialSubmitDisabling={false}
                />
            )}
            {showUnbookingModal && (
                <ConfirmationModal
                    closeModal={() => toggleUnbookingModal()}
                    title={Translate('view-order/basic-order-info/unbook/modal/title')}
                    text={unbookingModalMessage}
                    doButtonLabel={Translate(
                        'view-order/basic-order-info/unbook/modal/unbook-button'
                    )}
                    cancelButtonLabel={Translate(
                        'view-order/basic-order-info/unbook/modal/cancel-button'
                    )}
                    doButtonStyle={'danger'}
                    do={() => performUnbooking()}
                    disableSubmit={true}
                    initialSubmitDisabling={false}
                />
            )}
            {feedbackEntries.map((feedbackEntry, stackIndex) => {
                const snackbarProps = {
                    open: true,
                    stackIndex,
                    text: feedbackEntry.text,
                    onClose: () => dismissFeedback(feedbackEntry.id)
                };
                if (feedbackEntry.severity === 'success') {
                    return <SuccessSnackbar key={feedbackEntry.id} {...snackbarProps} />;
                }
                if (feedbackEntry.severity === 'warning') {
                    return <WarningSnackbar key={feedbackEntry.id} {...snackbarProps} />;
                }
                return <FailureSnackbar key={feedbackEntry.id} {...snackbarProps} />;
            })}
        </div>
    );
};

const BasicOrderInfoPageScopedToOrder = (props: any) => (
    <BasicOrderInfoPage key={props.orderId} {...props} />
);

export default withRouter(BasicOrderInfoPageScopedToOrder);
